import * as React from 'react';
import { trpc } from './trpc';
import { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { AppRouter } from '@tagadapay/trpc-types';

type OutputRouter = inferRouterOutputs<AppRouter>;
type InputRouter = inferRouterInputs<AppRouter>;

export type FastSearchInput = InputRouter['fastSearch']['search'];
export type FastSearchOutput = OutputRouter['fastSearch']['search'];
export type SearchResult = FastSearchOutput['results'][number];

/**
 * Hook for performing fast search across multiple entities
 */
export function useFastSearch(
    params: FastSearchInput,
    options?: {
        enabled?: boolean;
    }
) {
    return trpc.fastSearch.search.useQuery(params, {
        enabled: options?.enabled !== false && params.query.length > 0,
        // Debounce the search to avoid too many requests
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60, // 1 minute
    });
}

/**
 * Hook for performing fast search with debouncing
 */
export function useDebouncedFastSearch(
    query: string,
    limit?: number,
    debounceMs: number = 300
) {
    const [debouncedQuery, setDebouncedQuery] = React.useState(query);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query);
        }, debounceMs);

        return () => clearTimeout(timer);
    }, [query, debounceMs]);

    return useFastSearch(
        { query: debouncedQuery, limit },
        { enabled: debouncedQuery.length >= 2 }
    );
}
